import { CalendarIcon } from "lucide-react";
import { useCallback, useMemo } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DateOption {
  value: string;
  label: string;
}

const dateOptions: DateOption[] = [
  { value: "today", label: "Today" },
  { value: "week", label: "Past week" },
  { value: "month", label: "Past month" },
  { value: "quarter", label: "Past 3 months" },
  { value: "year", label: "Past year" },
];

interface DateRangeFilterProps {
  value: { start?: string; end?: string } | null;
  onChange: (range: { start?: string; end?: string } | null) => void;
  placeholder?: string;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  placeholder = "Date",
}) => {
  const calculateDateRange = useCallback((range: string): { start: string; end: string } | null => {
    if (range === "clear") {
      return null;
    }

    const now = new Date();
    let start: string | undefined;

    switch (range) {
      case "today":
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
        break;
      case "week":
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case "month":
        start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()).toISOString();
        break;
      case "quarter":
        start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()).toISOString();
        break;
      case "year":
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString();
        break;
      default:
        return null;
    }

    return start ? { start, end: now.toISOString() } : null;
  }, []);

  const handleDateSelect = useCallback(
    (range: string) => {
      const dateRange = calculateDateRange(range);
      onChange(dateRange);
    },
    [calculateDateRange, onChange],
  );

  const getCurrentValue = useCallback(() => {
    if (!value || !value.start) {
      return "";
    }

    const now = new Date();
    const start = new Date(value.start);
    const today = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const quarterAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

    const isSameDay = (date1: Date, date2: Date) =>
      Math.abs(date1.getTime() - date2.getTime()) < 24 * 60 * 60 * 1000;

    if (isSameDay(start, today)) {
      return "today";
    }
    if (isSameDay(start, weekAgo)) {
      return "week";
    }
    if (isSameDay(start, monthAgo)) {
      return "month";
    }
    if (isSameDay(start, quarterAgo)) {
      return "quarter";
    }
    if (isSameDay(start, yearAgo)) {
      return "year";
    }

    return "custom";
  }, [value]);

  const currentValue = getCurrentValue();

  const displayValue = useMemo(() => {
    const option = dateOptions.find(opt => opt.value === currentValue);
    return option ? option.label : placeholder;
  }, [currentValue, placeholder]);

  const hasActiveFilter = value && value.start;

  return (
    <Select value={currentValue} onValueChange={handleDateSelect}>
      <SelectTrigger
        className={`h-8 w-auto min-w-[100px] gap-1.5 border-0 bg-transparent px-2 py-1 text-sm font-medium shadow-none transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:ring-0 ${
          hasActiveFilter ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900"
        }`}
        aria-label={`Filter by date${hasActiveFilter ? " (active)" : ""}`}
      >
        <CalendarIcon className="h-4 w-4" />
        <SelectValue placeholder={placeholder}>{displayValue}</SelectValue>
      </SelectTrigger>
      <SelectContent align="start" className="w-48">
        {dateOptions.map(option => (
          <SelectItem key={option.value} value={option.value} className="text-sm">
            {option.label}
          </SelectItem>
        ))}
        {hasActiveFilter && (
          <>
            <div className="my-1 h-px bg-gray-200" />
            <SelectItem value="clear" className="text-sm text-gray-500">
              Clear filter
            </SelectItem>
          </>
        )}
      </SelectContent>
    </Select>
  );
};

export default DateRangeFilter;
